import { Routes, Route, Navigate, useLocation } from 'react-router';
import { Suspense, useEffect } from 'react';
import { useAuthStore } from './store/authStore';
import { useThemeStore } from './store/themeStore';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/layout/Layout';
import AuthLayout from './components/layout/AuthLayout';
import AdminLayout from './components/layout/AdminLayout';
import { lazyWithRetry as lazy, clearChunkReloadFlag } from './lib/lazyWithRetry';

// Eager pages — first paint critical (landing, auth entry, post-login dashboard,
// the analysis screen users hit constantly).
import Login from './pages/auth/Login';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Analysis from './pages/Analysis';

// Lazy pages — split into separate chunks, fetched on demand.
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback'));
const CheckEmail = lazy(() => import('./pages/auth/CheckEmail'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const MagicLink = lazy(() => import('./pages/auth/MagicLink'));
const MagicLogin = lazy(() => import('./pages/auth/MagicLogin'));
const Contact = lazy(() => import('./pages/Contact'));
const History = lazy(() => import('./pages/History'));
const Pricing = lazy(() => import('./pages/Pricing'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const RiskDisclaimer = lazy(() => import('./pages/legal/RiskDisclaimer'));
const TradingSignalsHelp = lazy(() => import('./pages/help/TradingSignals'));
const Settings = lazy(() => import('./pages/Settings'));
const Signals = lazy(() => import('./pages/Signals'));
const ExpertsList = lazy(() => import('./pages/experts/ExpertsList'));
const ExpertProfile = lazy(() => import('./pages/experts/ExpertProfile'));

// Admin chunk
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminTraders = lazy(() => import('./pages/admin/AdminTraders'));
const AdminSignals = lazy(() => import('./pages/admin/AdminSignals'));
const AdminJournal = lazy(() => import('./pages/admin/AdminJournal'));
const AdminCoupons = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminReferrals = lazy(() => import('./pages/admin/AdminReferrals'));
const AdminInsights = lazy(() => import('./pages/admin/AdminInsights'));
const AdminPlans = lazy(() => import('./pages/admin/AdminPlans'));
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions'));
const AdminConfig = lazy(() => import('./pages/admin/AdminConfig'));
const CreateSignal = lazy(() => import('./pages/admin/CreateSignal'));
const AnalysisConfigs = lazy(() => import('./pages/admin/AnalysisConfigs'));
const AnalysisRuns = lazy(() => import('./pages/admin/AnalysisRuns'));
const PromptTemplates = lazy(() => import('./pages/admin/PromptTemplates'));
const AdminLaunchMode = lazy(() => import('./pages/admin/AdminLaunchMode'));
const ModelBenchmark = lazy(() => import('./pages/admin/ModelBenchmark'));
const BacktestDashboard = lazy(() => import('./pages/admin/BacktestDashboard'));

// Checkout chunk
const TrialRegister = lazy(() => import('./pages/checkout/TrialRegister'));
const CheckoutSuccess = lazy(() => import('./pages/checkout/CheckoutSuccess'));
const CheckoutCancel = lazy(() => import('./pages/checkout/CheckoutCancel'));

// Help chunk
const HelpLayout = lazy(() => import('./components/help/HelpLayout'));
const HelpIndex = lazy(() => import('./pages/help/HelpIndex'));
const HelpArticle = lazy(() => import('./pages/help/HelpArticle'));

// Journal chunk
const JournalLayout = lazy(() => import('./components/journal/JournalLayout'));
const JournalDashboard = lazy(() => import('./pages/journal/JournalDashboard'));
const TradeLog = lazy(() => import('./pages/journal/TradeLog'));
const AddTrade = lazy(() => import('./pages/journal/AddTrade'));
const TradeDetail = lazy(() => import('./pages/journal/TradeDetail'));
const CsvImport = lazy(() => import('./pages/journal/CsvImport'));
const Playbooks = lazy(() => import('./pages/journal/Playbooks'));
const Reports = lazy(() => import('./pages/journal/Reports'));

// Shared spinner used both by auth gates and Suspense boundaries.
function FullPageSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin w-8 h-8 border-2 border-accent-primary border-t-transparent rounded-full" />
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, checkAuth } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Public route that redirects authenticated users to dashboard
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Admin route wrapper - checks for admin/expert role
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user, checkAuth } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has admin or expert role
  if (user?.role !== 'admin' && user?.role !== 'expert') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { theme } = useThemeStore();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // We mounted, so the chunk graph is healthy — reset the recovery flag so a
  // future stale-chunk after a deploy can trigger another one-shot reload.
  useEffect(() => {
    clearChunkReloadFlag();
  }, []);

  return (
    <ErrorBoundary>
      <Suspense fallback={<FullPageSpinner />}>
        <Routes>
          {/* Auth routes - redirect to dashboard if already logged in */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/check-email" element={<CheckEmail />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/magic-link" element={<MagicLink />} />
            <Route path="/magic-login" element={<MagicLogin />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
          </Route>

          {/* Public routes */}
          <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/checkout/trial" element={<TrialRegister />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/risk-disclaimer" element={<RiskDisclaimer />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/help/trading-signals" element={<TradingSignalsHelp />} />

          {/* Protected routes — all share the Layout shell (sidebar + topbar) */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/analysis" element={<Analysis />} />
            <Route path="/history" element={<History />} />
            <Route path="/signals" element={<Signals />} />
            <Route path="/experts" element={<ExpertsList />} />
            <Route path="/experts/:id" element={<ExpertProfile />} />
            <Route path="/settings" element={<Settings />} />
            {/* Help routes */}
            <Route path="/help" element={<HelpLayout />}>
              <Route index element={<HelpIndex />} />
              <Route path="admin/:adminSlug" element={<HelpArticle />} />
              <Route path=":slug" element={<HelpArticle />} />
            </Route>

            {/* Journal routes — JournalLayout now provides only the subnav */}
            <Route path="/journal" element={<JournalLayout />}>
              <Route index element={<JournalDashboard />} />
              <Route path="trades" element={<TradeLog />} />
              <Route path="trades/new" element={<AddTrade />} />
              <Route path="trades/:id" element={<TradeDetail />} />
              <Route path="import" element={<CsvImport />} />
              <Route path="playbooks" element={<Playbooks />} />
              <Route path="reports" element={<Reports />} />
              {/* Legacy redirect: /journal/stats → /journal/reports */}
              <Route path="stats" element={<Navigate to="/journal/reports" replace />} />
            </Route>
          </Route>

          {/* Admin routes with dedicated layout */}
          <Route
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/traders" element={<AdminTraders />} />
            <Route path="/admin/signals" element={<AdminSignals />} />
            <Route path="/admin/signals/create" element={<CreateSignal />} />
            <Route path="/admin/journal" element={<AdminJournal />} />
            <Route path="/admin/coupons" element={<AdminCoupons />} />
            <Route path="/admin/referrals" element={<AdminReferrals />} />
            <Route path="/admin/insights" element={<AdminInsights />} />
            <Route path="/admin/plans" element={<AdminPlans />} />
            <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
            <Route path="/admin/config" element={<AdminConfig />} />
            <Route path="/admin/analysis-configs" element={<AnalysisConfigs />} />
            <Route path="/admin/analysis-runs" element={<AnalysisRuns />} />
            <Route path="/admin/prompts" element={<PromptTemplates />} />
            <Route path="/admin/launch-mode" element={<AdminLaunchMode />} />
            <Route path="/admin/model-benchmark" element={<ModelBenchmark />} />
            <Route path="/admin/backtest" element={<BacktestDashboard />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
